<script>
import RexModalLabel from '@/rex-components/rex-modal-label.vue';
import RexModalInput from '@/rex-components/rex-modal-input.vue';
import RexButton from '@/rex-components/rex-button.vue';
import RexSearchSelect from '@/rex-components/RexSearchSelect.vue';
import RexInfoLabel from '@/rex-components/rex-info-label.vue';
import { CustomService } from '@/services/api/custom.service';
import { mapGetters, mapMutations } from 'vuex';

export default {
  components: {
    RexModalInput,
    RexModalLabel,
    RexButton,
    RexSearchSelect,
    RexInfoLabel,
  },
  computed: {
    ...mapGetters('logState/logCommonState', [
      'getWidgetAddDashboardInfo',
      'getWidgetFormData',
    ]),
    getIsNewDash() {
      return this.getWidgetFormData.isNewDash;
    },
    getDashboardFiledLabel() {
      return this.getIsNewDash
        ? this.$t('log.copyTemplate')
        : this.$t('log.selectDashboard');
    },
    getWidgetNameValid() {
      return this.getWidgetFormData.isWidgetNameValid;
    },
    getDashNameValid() {
      return this.getWidgetFormData.isDashNameValid;
    },
    getDashboardValid() {
      return this.getWidgetFormData.isDashboardValid;
    },
  },
  data() {
    return {
      dashboardList: [],
    };
  },
  methods: {
    ...mapMutations('logState/logCommonState', [
      'setAddDashboardInfoFields',
      'setWidgetInfoFields',
      'setWidgetFormData',
    ]),
    async getDashboards() {
      try {
        const { data } = await CustomService.getAllDashboards({
          page: 0,
          size: 10000,
          searchKeyword: '',
        });
        let dashList = data.data
          .map((item) => ({
            name: item.name,
            id: item.id,
          }))
          .sort((a, b) => (a.name > b.name ? 1 : -1));
        this.dashboardList = dashList;
      } catch (error) {
        console.log('error', error);
        this.dashboardList = [];
      }
    },
    onDashCreateChange(isNewDash) {
      this.setWidgetFormData({
        field: 'isNewDash',
        value: isNewDash,
      });
      if (isNewDash && !this.getWidgetFormData.isDashboardValid) {
        this.setWidgetFormData({
          field: 'isDashboardValid',
          value: true,
        });
      }
    },
  },
  created() {
    this.getDashboards();
  },
};
</script>

<template>
  <div>
    <div class="add-widget-dash-toggle">
      <RexButton
        :variant="!getIsNewDash ? 'primary' : 'secondary'"
        class="add-widget-dashboard-toggle"
        :class="getIsNewDash && 'font-primary'"
        id="add-widget-existing-dashboard"
        :styling="{ width: '10.75rem' }"
        @onClick="() => onDashCreateChange(false)"
      >
        {{ $t('log.existingDashboard') }}
      </RexButton>
      <RexButton
        :variant="getIsNewDash ? 'primary' : 'secondary'"
        class="add-widget-dashboard-toggle"
        :class="!getIsNewDash && 'font-primary'"
        id="add-widget-new-dashboard"
        :styling="{ width: '10.75rem' }"
        @onClick="() => onDashCreateChange(true)"
      >
        {{ $t('log.newDashboard') }}
      </RexButton>
    </div>

    <div>
      <div class="add-dashboard-input-fields">
        <RexModalLabel
          :primaryLabel="$t('log.widgetName')"
          :secondaryLabel="$t('required')"
          labelForId="log-add-widget-form-widget-name"
        />
        <RexModalInput
          style="height: 2.45rem"
          type="text"
          :placeholder="$t('log.enterName')"
          :modelValue="getWidgetAddDashboardInfo.widgetName"
          @update:modelValue="
            (value) => setAddDashboardInfoFields({ field: 'widgetName', value })
          "
          id="log-add-widget-form-widget-name"
          class="add-dash-field"
        />
        <RexInfoLabel
          v-if="!getWidgetNameValid"
          :isError="true"
          :info="$t('log.enterValidWidgetName')"
        />
      </div>

      <div v-if="getIsNewDash">
        <div class="add-dashboard-input-fields">
          <RexModalLabel
            :primaryLabel="$t('log.dashboardName')"
            :secondaryLabel="$t('required')"
            labelForId="log-add-widget-form-dashboard-name"
          />
          <RexModalInput
            style="height: 2.45rem"
            type="text"
            :placeholder="$t('log.enterName')"
            :modelValue="getWidgetAddDashboardInfo.dashboardName"
            @update:modelValue="
              (value) =>
                setAddDashboardInfoFields({ field: 'dashboardName', value })
            "
            id="log-add-widget-form-dashboard-name"
            class="add-dash-field"
          />
          <RexInfoLabel
            v-if="!getDashNameValid"
            :isError="true"
            :info="$t('log.enterValidDashboardName')"
          />
        </div>
      </div>

      <div class="add-dashboard-input-fields">
        <RexModalLabel
          :primaryLabel="getDashboardFiledLabel"
          :secondaryLabel="getIsNewDash ? $t('optional') : $t('required')"
          labelForId="log-add-widget-form-dashboard-field"
        />
        <RexSearchSelect
          :data="dashboardList"
          :current="getWidgetAddDashboardInfo.dashboard"
          @onChoose="
            (value) => setAddDashboardInfoFields({ field: 'dashboard', value })
          "
          :searchPlaceholder="$t('log.searchDashboard')"
          :objectValueIndex="0"
          id="log-add-widget-form-dashboard-field"
          width="100%"
          class="add-dash-field"
        />
        <RexInfoLabel
          v-if="!getDashboardValid"
          :isError="true"
          :info="$t('log.pleaseSelectDashboard')"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.add-dashboard-input-fields {
  margin-bottom: 1.75rem;
}
.add-widget-dash-toggle {
  @include flex-center-justify;
  margin-bottom: 1.75rem;
}
.add-widget-dashboard-toggle {
  @include dropshadow-effect($opacity: 0);
  font-size: 1rem;
  padding: 0rem;
  border-top: solid 0.0625em $border-primary;
  border-bottom: solid 0.0625em $border-primary;
  border-left: solid 0.0625em $border-primary;
  border-right: 0rem;
  border-radius: 0rem;
}
.add-widget-dashboard-toggle:first-child {
  border-top-left-radius: 0.25em;
  border-bottom-left-radius: 0.25em;
}
.add-widget-dashboard-toggle:last-child {
  border-top-right-radius: 0.25em;
  border-bottom-right-radius: 0.25em;
  border-right: solid 0.0625em $border-primary;
}
.add-dash-field {
  margin-bottom: 0.625rem;
}
.font-primary {
  color: $font-primary;
}
.font-primary:hover {
  color: $color-brand-accent;
}
:deep(.select-box) {
  width: 100%;
}
</style>
